import React from "react";
import { Link } from "react-router-dom";
import { Trash2, Minus, Plus, ArrowLeft, MessageCircle } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { buildWhatsAppOrderLink } from "@/lib/brand";

const Cart = () => {
  const { items, updateQty, removeItem, subtotal, clearCart, count } = useCart();

  const handleWhatsApp = () => {
    if (items.length === 0) return;
    const link = buildWhatsAppOrderLink(items, subtotal);
    window.open(link, "_blank", "noopener,noreferrer");
  };

  return (
    <div data-testid="cart-page" className="bg-[#F9F6F0] min-h-[60vh]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <p className="text-xs tracking-[0.3em] uppercase text-[#C5A059] font-semibold">Votre commande</p>
        <h1 className="font-heading text-5xl md:text-6xl text-[#1A1617] mt-3">
          Votre <em className="italic text-[#5B2333]">panier</em>
        </h1>

        {items.length === 0 ? (
          <div className="mt-16 border border-[#E8E2D2] bg-white p-16 text-center" data-testid="cart-empty">
            <p className="font-heading text-3xl text-[#1A1617]">Votre panier est vide</p>
            <p className="text-sm text-[#5C5651] mt-3">Faites votre sélection parmi nos cuvées des Pouilles.</p>
            <Link
              to="/vins"
              data-testid="cart-empty-cta"
              className="inline-flex items-center gap-2 mt-8 bg-[#5B2333] text-white px-8 py-4 text-sm tracking-[0.18em] uppercase hover:bg-[#4A1525] transition-colors"
            >
              <ArrowLeft className="w-4 h-4" /> Découvrir la cave
            </Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-12 gap-10 mt-12">
            <div className="lg:col-span-8 bg-white border border-[#E8E2D2]" data-testid="cart-items">
              <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 border-b border-[#E8E2D2] text-xs tracking-[0.2em] uppercase text-[#5C5651] font-semibold">
                <div className="col-span-6">Produit</div>
                <div className="col-span-2 text-right">Prix</div>
                <div className="col-span-2 text-center">Quantité</div>
                <div className="col-span-2 text-right">Sous-total</div>
              </div>

              {items.map((i) => (
                <div
                  key={i.id}
                  data-testid={`cart-row-${i.id}`}
                  className="grid grid-cols-12 gap-4 px-6 py-6 border-b border-[#E8E2D2] last:border-b-0 items-center"
                >
                  <div className="col-span-12 md:col-span-6 flex items-center gap-4">
                    <img src={i.image} alt={i.name} className="w-16 h-20 object-contain bg-[#F9F6F0] p-1" />
                    <div>
                      <Link to={`/vin/${i.id}`} className="font-heading text-lg text-[#1A1617] hover:text-[#5B2333]">{i.name}</Link>
                      <p className="text-xs tracking-[0.15em] uppercase text-[#5C5651] mt-1">{i.region}</p>
                    </div>
                  </div>
                  <div className="col-span-4 md:col-span-2 md:text-right text-sm text-[#1A1617]">{i.price.toFixed(2)}€</div>
                  <div className="col-span-5 md:col-span-2 flex md:justify-center">
                    <div className="flex items-center border border-[#E8E2D2]">
                      <button
                        type="button"
                        onClick={() => updateQty(i.id, i.qty - 1)}
                        data-testid={`cart-decrement-${i.id}`}
                        className="w-8 h-9 flex items-center justify-center hover:bg-[#F9F6F0] transition"
                        aria-label="Diminuer"
                      >
                        <Minus className="w-3 h-3" strokeWidth={1.5} />
                      </button>
                      <span data-testid={`cart-qty-${i.id}`} className="w-9 text-center text-sm">{i.qty}</span>
                      <button
                        type="button"
                        onClick={() => updateQty(i.id, i.qty + 1)}
                        data-testid={`cart-increment-${i.id}`}
                        className="w-8 h-9 flex items-center justify-center hover:bg-[#F9F6F0] transition"
                        aria-label="Augmenter"
                      >
                        <Plus className="w-3 h-3" strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                  <div className="col-span-2 md:col-span-1 text-right font-heading text-lg text-[#5B2333]" data-testid={`cart-row-subtotal-${i.id}`}>
                    {(i.qty * i.price).toFixed(2)}€
                  </div>
                  <div className="col-span-1 flex justify-end">
                    <button
                      type="button"
                      onClick={() => removeItem(i.id)}
                      data-testid={`cart-remove-${i.id}`}
                      aria-label={`Retirer ${i.name}`}
                      className="text-[#5C5651] hover:text-[#5B2333] p-1"
                    >
                      <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
              ))}

              <div className="flex items-center justify-between px-6 py-4 bg-[#F9F6F0]">
                <Link
                  to="/vins"
                  data-testid="continue-shopping"
                  className="inline-flex items-center gap-2 text-sm text-[#5C5651] hover:text-[#5B2333]"
                >
                  <ArrowLeft className="w-4 h-4" /> Continuer mes achats
                </Link>
                <button
                  type="button"
                  onClick={clearCart}
                  data-testid="cart-clear"
                  className="text-xs tracking-[0.18em] uppercase text-[#5C5651] hover:text-[#5B2333]"
                >
                  Vider le panier
                </button>
              </div>
            </div>

            {/* Summary */}
            <aside className="lg:col-span-4 bg-white border border-[#E8E2D2] p-8 h-fit" data-testid="cart-summary">
              <h2 className="font-heading text-2xl text-[#1A1617]">Récapitulatif</h2>
              <dl className="mt-6 space-y-3 text-sm">
                <div className="flex justify-between">
                  <dt className="text-[#5C5651]">Articles</dt>
                  <dd className="text-[#1A1617]">{count}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-[#5C5651]">Sous-total</dt>
                  <dd className="text-[#1A1617]" data-testid="cart-subtotal">{subtotal.toFixed(2)}€</dd>
                </div>
                <div className="flex justify-between border-t border-[#E8E2D2] pt-4 mt-4">
                  <dt className="font-heading text-lg text-[#1A1617]">Total</dt>
                  <dd className="font-heading text-2xl text-[#5B2333]" data-testid="cart-total">{subtotal.toFixed(2)}€</dd>
                </div>
              </dl>

              <p className="text-xs text-[#5C5651] mt-4 leading-relaxed">
                Les frais de livraison sont confirmés par WhatsApp lors de la commande.
                Paiement en espèces à la livraison.
              </p>

              <button
                type="button"
                onClick={handleWhatsApp}
                data-testid="checkout-whatsapp"
                className="w-full mt-8 inline-flex items-center justify-center gap-2 bg-[#25D366] text-white px-6 py-4 text-sm tracking-[0.18em] uppercase font-semibold hover:opacity-90 transition-opacity"
              >
                <MessageCircle className="w-5 h-5" strokeWidth={1.8} />
                Commander via WhatsApp
              </button>
              <p className="text-[11px] text-[#5C5651] text-center mt-3 italic">
                Votre commande s&apos;ouvrira pré-remplie dans WhatsApp.
              </p>
            </aside>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cart;
